import {
  ClinicianPortalApiRequest,
  PortalOperation,
  PortalPatient,
  PortalTherapist,
} from "./contracts";

export interface ClinicianPortalCacheScope {
  clinicianCode: string;
  database: string;
  edition: string;
  sessionKey?: string;
}

export interface ClientSnapshot {
  patient: PortalPatient;
  therapist: PortalTherapist | null;
}

interface RequestEntry<T> {
  request: ClinicianPortalApiRequest;
  value: T;
  expiresAt: number;
}

interface InFlightEntry<T> {
  request: ClinicianPortalApiRequest;
  controller: AbortController;
  promise: Promise<T>;
}

const REFERENCE_TTLS: Partial<Record<PortalOperation, number>> = {
  "portal.getActivityTypes": 15 * 60 * 1000,
  "portal.admin.getLovOptions": 10 * 60 * 1000,
  "portal.getRecordFormConfig": 15 * 60 * 1000,
  "portal.getRelationOptions": 15 * 60 * 1000,
  "portal.getRankLevels": 15 * 60 * 1000,
  "portal.getActivityTemplates": 5 * 60 * 1000,
};

const READ_OPERATIONS = new Set<PortalOperation>([
  "portal.askClinicianAssistant",
  "portal.getMyClients",
  "portal.getTablePreferences",
  "portal.supervisor.getTeamOverview",
  "portal.supervisor.getTeamClients",
  "portal.supervisor.getTeamClientDetail",
  "portal.supervisor.getTeamHomework",
  "portal.supervisor.getTeamHomeworkDetail",
  "portal.supervisor.getTeamReports",
  "portal.supervisor.getTeamAttention",
  "portal.getClient",
  "portal.getRecordFormConfig",
  "portal.getRelationOptions",
  "portal.getClientValues",
  "portal.reports.getValueEngagement",
  "portal.reports.getHomeworkOverview",
  "portal.reports.getCompletionByDevice",
  "portal.reports.getDashboardAttention",
  "portal.getRankLevels",
  "portal.getClientRankLevelRules",
  "portal.getCopingSkills",
  "portal.getClientCopingSkills",
  "portal.getCurrentHomework",
  "portal.getHomeworkHistory",
  "portal.getClientSessions",
  "portal.getClientSessionTranscript",
  "portal.getCaseloadEngagement",
  "portal.getDailyMoodChecks",
  "portal.getYouthTopProblemRatings",
  "portal.getExpectancies",
  "portal.getHomeworkAnalyses",
  "portal.admin.getActivityRegistry",
  "portal.admin.getLovCatalogs",
  "portal.admin.getLovOptions",
  "portal.admin.getClinics",
  "portal.admin.getTherapists",
  "portal.admin.getAllClients",
  "portal.getHomeworkDetail",
  "portal.getActivityTemplates",
  "portal.getActivityTemplate",
  "portal.getActivityTypes",
  "portal.getHomeworkActivities",
]);

function stableValue(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(stableValue);
  if (value && typeof value === "object") {
    return Object.keys(value as Record<string, unknown>)
      .sort()
      .reduce<Record<string, unknown>>((result, key) => {
        if (key !== "clinicianCode") {
          result[key] = stableValue((value as Record<string, unknown>)[key]);
        }
        return result;
      }, {});
  }
  return value;
}

function requestKey(request: ClinicianPortalApiRequest): string {
  return JSON.stringify(stableValue(request));
}

function sameScope(
  first: ClinicianPortalCacheScope | undefined,
  second: ClinicianPortalCacheScope,
): boolean {
  return Boolean(
    first &&
      first.clinicianCode === second.clinicianCode &&
      first.database === second.database &&
      first.edition === second.edition &&
      (first.sessionKey || "") === (second.sessionKey || ""),
  );
}

function abortError(): Error {
  const error = new Error("The request was aborted.");
  error.name = "AbortError";
  return error;
}

function forConsumer<T>(promise: Promise<T>, signal?: AbortSignal): Promise<T> {
  if (!signal) return promise;
  if (signal.aborted) return Promise.reject(abortError());

  return new Promise<T>((resolve, reject) => {
    const handleAbort = () => reject(abortError());
    signal.addEventListener("abort", handleAbort, { once: true });
    promise.then(
      (value) => {
        signal.removeEventListener("abort", handleAbort);
        resolve(value);
      },
      (error) => {
        signal.removeEventListener("abort", handleAbort);
        reject(error);
      },
    );
  });
}

function requestField(
  request: ClinicianPortalApiRequest,
  field: string,
): unknown {
  return (request as unknown as Record<string, unknown>)[field];
}

function matchesPatient(
  request: ClinicianPortalApiRequest,
  patientId: string | undefined,
): boolean {
  const requestPatientId = requestField(request, "patientId");
  return !patientId || !requestPatientId || requestPatientId === patientId;
}

function matchesTemplate(
  request: ClinicianPortalApiRequest,
  templateId: string | undefined,
): boolean {
  const requestTemplateId =
    requestField(request, "templateId") ||
    requestField(request, "activityTemplateId");
  return !templateId || !requestTemplateId || requestTemplateId === templateId;
}

function invalidatedReadOperations(
  mutation: ClinicianPortalApiRequest,
): Set<PortalOperation> {
  const operation = mutation.operation;

  if (operation === "portal.saveTablePreferences") {
    return new Set(["portal.getTablePreferences"]);
  }

  if (
    operation === "portal.createClient" ||
    operation === "portal.updateClient"
  ) {
    return new Set([
      "portal.getMyClients",
      "portal.admin.getAllClients",
      "portal.supervisor.getTeamClients",
      "portal.supervisor.getTeamClientDetail",
      "portal.supervisor.getTeamReports",
      "portal.supervisor.getTeamAttention",
      "portal.getClient",
      "portal.reports.getDashboardAttention",
    ]);
  }
  if (
    operation === "portal.createClientValue" ||
    operation === "portal.updateClientValue" ||
    operation === "portal.setClientValueEnabled"
  ) {
    return new Set([
      "portal.getClientValues",
      "portal.reports.getValueEngagement",
      "portal.supervisor.getTeamReports",
    ]);
  }
  if (
    operation === "portal.createClientCopingSkill" ||
    operation === "portal.updateClientCopingSkill" ||
    operation === "portal.setClientCopingSkillEnabled"
  ) {
    return new Set(["portal.getClientCopingSkills"]);
  }
  if (
    operation === "portal.createClientRankLevelRule" ||
    operation === "portal.updateClientRankLevelRule" ||
    operation === "portal.setClientRankLevelRuleEnabled"
  ) {
    return new Set(["portal.getClientRankLevelRules", "portal.getClient"]);
  }
  if (
    operation === "portal.createCopingSkill" ||
    operation === "portal.updateCopingSkill" ||
    operation === "portal.setCopingSkillEnabled"
  ) {
    return new Set(["portal.getCopingSkills"]);
  }
  if (
    operation === "portal.createActivityTemplate" ||
    operation === "portal.updateActivityTemplate" ||
    operation === "portal.updateActivityTemplateSteps" ||
    operation === "portal.uploadActivityTemplateImage" ||
    operation === "portal.deleteActivityTemplateImage"
  ) {
    return new Set([
      "portal.getActivityTemplates",
      "portal.getActivityTemplate",
    ]);
  }
  if (
    operation === "portal.admin.createLovOption" ||
    operation === "portal.admin.updateLovOption" ||
    operation === "portal.admin.setLovOptionEnabled"
  ) {
    return new Set([
      "portal.admin.getLovOptions",
      "portal.getRecordFormConfig",
      "portal.getActivityTypes",
    ]);
  }
  if (
    operation === "portal.admin.createClinic" ||
    operation === "portal.admin.updateClinic" ||
    operation === "portal.admin.createTherapist" ||
    operation === "portal.admin.updateTherapist"
  ) {
    return new Set([
      "portal.admin.getClinics",
      "portal.admin.getTherapists",
      "portal.admin.getAllClients",
      "portal.getRelationOptions",
      "portal.getMyClients",
      "portal.supervisor.getTeamOverview",
      "portal.supervisor.getTeamClients",
      "portal.supervisor.getTeamClientDetail",
      "portal.supervisor.getTeamHomework",
      "portal.supervisor.getTeamHomeworkDetail",
      "portal.supervisor.getTeamReports",
      "portal.supervisor.getTeamAttention",
    ]);
  }
  if (operation === "portal.generateHomeworkAnalysis") {
    return new Set(["portal.getHomeworkAnalyses"]);
  }
  if (
    operation === "portal.createHomework" ||
    operation === "portal.createHomeworkActivity" ||
    operation === "portal.selectHomeworkTemplate" ||
    operation === "portal.updateHomework" ||
    operation === "portal.updateHomeworkSteps" ||
    operation === "portal.uploadHomeworkImage" ||
    operation === "portal.deleteHomeworkImage" ||
    operation === "portal.duplicateHomework" ||
    operation === "portal.deleteHomework"
  ) {
    return new Set([
      "portal.getCurrentHomework",
      "portal.getHomeworkHistory",
      "portal.getHomeworkDetail",
      "portal.getHomeworkActivities",
      "portal.supervisor.getTeamHomework",
      "portal.supervisor.getTeamHomeworkDetail",
      "portal.supervisor.getTeamReports",
      "portal.supervisor.getTeamAttention",
      "portal.getClient",
      "portal.getCaseloadEngagement",
      "portal.reports.getValueEngagement",
      "portal.reports.getHomeworkOverview",
      "portal.reports.getCompletionByDevice",
      "portal.reports.getDashboardAttention",
    ]);
  }
  return new Set();
}

export class PortalRequestCache {
  private scope?: ClinicianPortalCacheScope;
  private readonly inFlight = new Map<string, InFlightEntry<unknown>>();
  private readonly references = new Map<string, RequestEntry<unknown>>();
  private readonly clientSnapshots = new Map<string, ClientSnapshot>();
  private readonly generations = new Map<string, number>();

  ensureScope(scope: ClinicianPortalCacheScope): void {
    const normalized = {
      ...scope,
      clinicianCode: scope.clinicianCode.trim(),
    };
    if (!sameScope(this.scope, normalized)) {
      this.clear();
      this.scope = normalized;
    }
  }

  clear(): void {
    this.inFlight.forEach((entry) => entry.controller.abort());
    this.inFlight.clear();
    this.references.clear();
    this.clientSnapshots.clear();
    this.generations.clear();
    this.scope = undefined;
  }

  isRead(operation: PortalOperation): boolean {
    return READ_OPERATIONS.has(operation);
  }

  referenceTtl(operation: PortalOperation): number {
    return REFERENCE_TTLS[operation] || 0;
  }

  invalidateRead(operation: PortalOperation): void {
    this.references.forEach((entry, key) => {
      if (entry.request.operation === operation) this.invalidateKey(key);
    });
    this.inFlight.forEach((entry, key) => {
      if (entry.request.operation === operation) this.invalidateKey(key);
    });
  }

  private invalidateKey(key: string): void {
    this.references.delete(key);
    this.inFlight.delete(key);
    this.generations.set(key, (this.generations.get(key) || 0) + 1);
  }

  read<T>({
    request,
    signal,
    execute,
  }: {
    request: ClinicianPortalApiRequest;
    signal?: AbortSignal;
    execute: (signal: AbortSignal) => Promise<T>;
  }): Promise<T> {
    const key = requestKey(request);
    const cached = this.references.get(key) as RequestEntry<T> | undefined;
    if (cached && cached.expiresAt > Date.now()) {
      return forConsumer(Promise.resolve(cached.value), signal);
    }
    if (cached) this.references.delete(key);

    const existing = this.inFlight.get(key) as InFlightEntry<T> | undefined;
    if (existing) return forConsumer(existing.promise, signal);

    const controller = new AbortController();
    const generation = this.generations.get(key) || 0;
    let promise!: Promise<T>;
    promise = (async () => {
      try {
        const value = await execute(controller.signal);
        const ttl = this.referenceTtl(request.operation);
        const isCurrentGeneration =
          (this.generations.get(key) || 0) === generation;
        if (ttl > 0 && isCurrentGeneration) {
          this.references.set(key, {
            request,
            value,
            expiresAt: Date.now() + ttl,
          });
        }
        if (
          isCurrentGeneration &&
          request.operation !== "portal.admin.getAllClients"
        ) {
          this.rememberClientSnapshots(
            value,
            request.accessScope === "team" ||
              request.operation === "portal.supervisor.getTeamClientDetail"
              ? "team"
              : "assigned",
          );
        }
        return value;
      } finally {
        if (this.inFlight.get(key)?.promise === promise) {
          this.inFlight.delete(key);
        }
      }
    })();

    this.inFlight.set(key, { request, controller, promise });
    return forConsumer(promise, signal);
  }

  rememberClientSnapshots(
    value: unknown,
    accessScope: "assigned" | "team" = "assigned",
  ): void {
    if (!value || typeof value !== "object") return;
    const data = value as Record<string, unknown>;
    if (
      data.success !== true ||
      !data.therapist ||
      typeof data.therapist !== "object"
    ) {
      return;
    }
    const therapist = data.therapist as PortalTherapist;
    if (Array.isArray(data.patients)) {
      data.patients.forEach((patient) => {
        if (
          patient &&
          typeof patient === "object" &&
          typeof patient.id === "string"
        ) {
          this.clientSnapshots.set(`${accessScope}:${patient.id}`, {
            patient: patient as PortalPatient,
            therapist,
          });
        }
      });
    }
    if (Array.isArray(data.clients)) {
      data.clients.forEach((client) => {
        if (
          client &&
          typeof client === "object" &&
          typeof client.id === "string"
        ) {
          this.clientSnapshots.set(`${accessScope}:${client.id}`, {
            patient: client as PortalPatient,
            therapist,
          });
        }
      });
    }
    if (
      data.authorized === true &&
      data.patient &&
      typeof data.patient === "object" &&
      typeof (data.patient as Record<string, unknown>).id === "string"
    ) {
      const patient = data.patient as PortalPatient;
      this.clientSnapshots.set(`${accessScope}:${patient.id}`, { patient, therapist });
    }
    if (
      data.authorized === true &&
      data.client &&
      typeof data.client === "object" &&
      typeof (data.client as Record<string, unknown>).id === "string"
    ) {
      const client = data.client as PortalPatient;
      this.clientSnapshots.set(`${accessScope}:${client.id}`, { patient: client, therapist });
    }
  }

  getClientSnapshot(
    patientId: string,
    accessScope: "assigned" | "team" = "assigned",
  ): ClientSnapshot | undefined {
    return this.clientSnapshots.get(`${accessScope}:${patientId}`);
  }

  invalidateAfterMutation(mutation: ClinicianPortalApiRequest): void {
    const operations = invalidatedReadOperations(mutation);
    if (!operations.size) return;

    const patientId = requestField(mutation, "patientId") as string | undefined;
    const templateId = requestField(mutation, "templateId") as
      | string
      | undefined;
    const catalogKey = requestField(mutation, "catalogKey");
    const shouldInvalidate = (request: ClinicianPortalApiRequest) => {
      if (!operations.has(request.operation)) return false;
      if (!matchesPatient(request, patientId)) return false;
      if (!matchesTemplate(request, templateId)) return false;
      if (
        request.operation === "portal.admin.getLovOptions" &&
        catalogKey &&
        requestField(request, "catalogKey") !== catalogKey
      ) {
        return false;
      }
      return true;
    };

    this.references.forEach((entry, key) => {
      if (shouldInvalidate(entry.request)) this.invalidateKey(key);
    });
    this.inFlight.forEach((entry, key) => {
      if (shouldInvalidate(entry.request)) this.invalidateKey(key);
    });

    if (
      mutation.operation === "portal.createClient" ||
      mutation.operation === "portal.updateClient"
    ) {
      if (patientId) {
        this.clientSnapshots.delete(`assigned:${patientId}`);
        this.clientSnapshots.delete(`team:${patientId}`);
      }
      else this.clientSnapshots.clear();
    }
    if (operations.has("portal.getClient") && patientId) {
      this.clientSnapshots.delete(`assigned:${patientId}`);
      this.clientSnapshots.delete(`team:${patientId}`);
    }
  }
}

export const clinicianPortalRequestCache = new PortalRequestCache();

export function prepareClinicianPortalCacheScope(
  clinicianCode: string,
  database = "supabase",
  edition = "clinician-portal",
  sessionKey = "",
): void {
  if (!clinicianCode.trim()) {
    clinicianPortalRequestCache.clear();
    return;
  }
  clinicianPortalRequestCache.ensureScope({
    clinicianCode,
    database,
    edition,
    sessionKey,
  });
}

export function getClinicianPortalClientSnapshot(
  patientId: string,
  accessScope: "assigned" | "team" = "assigned",
): ClientSnapshot | undefined {
  return clinicianPortalRequestCache.getClientSnapshot(patientId, accessScope);
}

export function invalidateClinicianPortalReferenceData(
  operation: PortalOperation,
): void {
  clinicianPortalRequestCache.invalidateRead(operation);
}
